import type { Entity } from '@backstage/catalog-model';
import { ErrorPanel, Progress } from '@backstage/core-components';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { HomePageWidgetBlueprint } from '@backstage/plugin-home-react/alpha';
import { useApi } from '@backstage/frontend-plugin-api';
import { LinearProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import useAsync from 'react-use/esm/useAsync';
import {
  BACKSTAGE_ADR_LOCATION,
  BACKSTAGE_TECHDOCS_REF,
  readAnnotation,
  WIB_DEPLOYMENT_MODE,
  WIB_TECH_DEBT,
} from '../../annotations';

export interface FilaSalud {
  etiqueta: string;
  cuenta: number;
  total: number;
}

/**
 * Calcula los indicadores de salud de gobernanza sobre los componentes.
 * TODO(Fase 2): sustituir por métricas de tech-insights.
 */
export function calcularSaludGobernanza(componentes: Entity[]): FilaSalud[] {
  const total = componentes.length;
  const contar = (predicado: (entidad: Entity) => boolean) =>
    componentes.filter(predicado).length;

  return [
    {
      etiqueta: 'Con documentación TechDocs',
      cuenta: contar(
        e => readAnnotation(e, BACKSTAGE_TECHDOCS_REF) !== undefined,
      ),
      total,
    },
    {
      etiqueta: 'Con registro de ADR',
      cuenta: contar(
        e => readAnnotation(e, BACKSTAGE_ADR_LOCATION) !== undefined,
      ),
      total,
    },
    {
      etiqueta: 'Desplegados fuera de bare-metal',
      cuenta: contar(e => {
        const modo = readAnnotation(e, WIB_DEPLOYMENT_MODE);
        return modo !== undefined && modo !== 'bare-metal';
      }),
      total,
    },
    {
      etiqueta: 'Sin wib/tech-debt registrado',
      cuenta: contar(e => readAnnotation(e, WIB_TECH_DEBT) === undefined),
      total,
    },
  ];
}

const useStyles = makeStyles(theme => ({
  fila: {
    marginBottom: theme.spacing(2),
  },
}));

export function GovernanceHealthWidgetContent() {
  const catalogApi = useApi(catalogApiRef);
  const classes = useStyles();

  const { value, loading, error } = useAsync(async () => {
    const respuesta = await catalogApi.getEntities({
      filter: { kind: 'Component' },
    });
    return calcularSaludGobernanza(respuesta.items);
  }, [catalogApi]);

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return <ErrorPanel error={error} />;
  }

  if (!value || value[0]?.total === 0) {
    return (
      <Typography variant="body2">
        No hay componentes en el catálogo todavía.
      </Typography>
    );
  }

  return (
    <>
      {value.map(fila => {
        const porcentaje = fila.total
          ? Math.round((fila.cuenta * 100) / fila.total)
          : 0;
        return (
          <div key={fila.etiqueta} className={classes.fila}>
            <Typography variant="body2">
              {fila.etiqueta} — {fila.cuenta}/{fila.total} ({porcentaje}%)
            </Typography>
            <LinearProgress variant="determinate" value={porcentaje} />
          </div>
        );
      })}
    </>
  );
}

export const governanceHealthWidget = HomePageWidgetBlueprint.make({
  name: 'governance-health',
  params: {
    name: 'governance-health',
    title: 'Salud de gobernanza',
    components: async () => ({ Content: GovernanceHealthWidgetContent }),
  },
});
